import React, { Fragment, useEffect, useState } from "react";
import PageHeader from "../../common/components/PageHeader";
import { Box, Button, Dialog, DialogContent, Divider, Grid, IconButton, Stack, TextField, Typography, useTheme } from "@mui/material";
import { Add, Close, FiberManualRecord, WatchLater } from "@mui/icons-material";
import CommonButton from "../../common/components/CommonButton";
import { ExamTypes, ServiceOffer, ServiceTypes, SkillPath, TutoringType, UserTypeId } from "../../common/const";
import Tutor from "./Tutor";
import SubjectOverview from "./SubjectOverview";
import Progress from "./Progress";
import SessionHistory from "./SessionHistory";
import Events from "./Events";
import Tasks from "./Tasks";
import Resource from "./Resource";
import CurrentPlan from "../../common/components/CurrentPlan";
import ProgrssBar from "./ProgressBar";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { getScoreStats } from "../../api/scoreApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { checkBookingSession, confirmBooking, getEventByInvite } from "../../api/sessionApi";
import CommonBookSession from "../../common/components/CommonBookSession";
import moment from "moment";
import styled from "@emotion/styled";
import { assignSubject, getAllAssignSubject, getAllSubject } from "../../api/subjectApi";
import CommonDialog from "../../common/components/CommonDialog";
import CommonSelect from "../../common/components/CommonSelect";

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        borderRadius: '16px',
        maxWidth: '540px',
        width: '100%',
    },
});

const DcpTutoring = () => {
    const theme = useTheme();
    const location = useLocation();
    const [tutoringType, setTutoringType] = useState(TutoringType.AP);
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const [progressData, setProgressData] = useState(null);
    const [assigned, setAssigned] = useState(null);
    const [mentorId, setMentorId] = useState(null);
    const [open, setOpen] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [eventData, setEventData] = useState(null);
    const [bookSessionDialog, setBookSessionDialog] = useState(false);
    const [sessionNote, setSessionNote] = useState('');
    const [assignSkill, setAssignSkill] = useState([]);
    const [addOpen, setAddOpen] = useState(false);
    const [subjectList, setSubjectList] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [subService, setSubService] = useState(null);

    const checkSessionBook = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await checkBookingSession(subService ? ServiceOffer.skillCoach : tutoringType === TutoringType.AP ? ServiceOffer.tutor : ServiceOffer.academic, user?.student_id, subService);
            setMentorId(response?.data?.mentor_profile_id);
            setAssigned(response?.data.type);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.student_id) {
            checkSessionBook();
        }
    }, [user, tutoringType, subService]);

    const getStats = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getScoreStats(subService ? ServiceOffer.skillCoach : ServiceTypes[tutoringType], user?.student_id, subService);
            dispatch(setLoading({
                loading: false
            }));
            setProgressData(response.data);
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    useEffect(() => {
        if (user?.student_id) {
            getStats();
        }
    }, [user, tutoringType, subService]);

    const getEventData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getEventByInvite(initialData?.payload?.invitee?.uri);
            setEventData(response.data);
            setBookSessionDialog(true);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    useEffect(() => {
        if (initialData !== null) {
            dispatch(setLoading({
                loading: true
            }));
            setTimeout(() => {
                getEventData();
            }, 5000);
        }
    }, [initialData]);

    const handleCloseAll = () => {
        setBookSessionDialog(false);
        setSessionNote('');
        setEventData(null);
    };

    const handleConfirmBooking = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const data = {
                "mentor_profile_id": mentorId,
                "student_id": user?.student_id,
                "service_offers": location.pathname === SkillPath ? [ServiceOffer.skillCoach] : [ServiceTypes[tutoringType]],
                "notes": sessionNote,
                "status": "CONFIRMED",
                "invitee_email": eventData.invitee_email,
                "calendly_event_uri": eventData.calendly_event_uri,
                "calendly_invitee_uri": eventData.calendly_invitee_uri,
                "student_subject_id": subService,
            }
            const response = await confirmBooking(data);
            if (response) {
                dispatch(setLoading({
                    loading: false
                }));
                setBookSessionDialog(false);
                setSessionNote(null);
                setEventData(null);
                setInitialData(null);
                showNotification('Session booked successfully', 'success');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const getAssignedSubject = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getAllAssignSubject(ServiceOffer.skillCoach, user?.student_id);
            setSubService(response?.data?.[0]?.student_subject_id);
            setAssignSkill(response?.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const getSubjects = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getAllSubject(
                1,
                100,
                'SKILL',
                ''
            );
            setSubjectList(response?.data?.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (error) {
            showNotification('Failed to load subjects', 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    useEffect(() => {
        if (addOpen) {
            getSubjects();
        }
    }, [addOpen]);

    useEffect(() => {
        if (location.pathname === SkillPath) {
            getAssignedSubject();
        }
    }, [location.pathname]);

    return (
        <Fragment>
            <CommonDialog
                open={addOpen}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Add Coding/Skill/Technology</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setAddOpen(false);
                                    setSelectedSubject(null);
                                }}
                            />
                        </Box>
                        <Box width="80%" mb={1}>
                            <Typography sx={{ fontSize: 12 }} color={theme.palette.background.dark}>Enter a subject to organize your content.</Typography>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Box mt={1} mb={1}>
                        <CommonSelect
                            label="Coding/Skill/Technology"
                            name="subjects"
                            value={selectedSubject}
                            onChange={(e) => setSelectedSubject(e.target.value)}
                            placeholder="Select Skill"
                            options={subjectList.map((a) => ({
                                label: a.name,
                                value: a.subject_id
                            }))}
                        />
                    </Box>
                }
                actionButtons={(
                    <CommonButton
                        label="Add Coding/Skill/Technology"
                        size="small"
                        startIcon={<Add style={{ fontSize: '15px' }} />}
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        handleClick={async () => {
                            if (selectedSubject !== null) {
                                try {
                                    dispatch(setLoading({
                                        loading: true
                                    }));
                                    const response = await assignSubject({
                                        "subject_id": selectedSubject,
                                        "type": ServiceOffer.skillCoach
                                    });
                                    if (response.success) {
                                        showNotification(response.message, 'success');
                                        setSelectedSubject(null);
                                        setAddOpen(false)
                                        getAssignedSubject();
                                        dispatch(setLoading({
                                            loading: false
                                        }));
                                    }
                                } catch (err) {
                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                    showNotification(errorMessage, 'error');
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                }
                            } else {
                                showNotification('Please select coding/skill/technology', 'error');
                            }
                        }}
                    />
                )}
            />
            <StyledDialog open={bookSessionDialog} onClose={handleCloseAll} maxWidth="sm" fullWidth>
                <DialogContent sx={{ p: 4, position: 'relative' }}>
                    <IconButton
                        onClick={handleCloseAll}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: '#6B7280',
                        }}
                    >
                        <Close />
                    </IconButton>

                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                        Confirm Your Session
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6}>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: '#F9FAFB',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <img
                                    src='/Images/graycalendar.png'
                                    style={{
                                        width: 20,
                                        position: 'relative'
                                    }}
                                    alt=''
                                />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {moment(eventData?.session_start_time).format('dddd, MMM YY')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                        Date
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: '#F9FAFB',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <WatchLater sx={{ color: '#6B7280', fontSize: 20 }} />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {moment(eventData?.session_start_time).format('hh:mm A')}- {moment(eventData?.session_end_time).format('hh:mm A')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                        Time
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            Any specific question for this session
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="Write here"
                            value={sessionNote}
                            onChange={(e) => setSessionNote(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                },
                            }}
                        />
                    </Box>

                    <Button
                        fullWidth
                        onClick={handleConfirmBooking}
                        sx={{
                            backgroundColor: '#1E3A8A',
                            color: 'white',
                            textTransform: 'none',
                            borderRadius: '8px',
                            height: '48px',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: '#1E40AF',
                            },
                        }}
                    >
                        Confirm Booking
                    </Button>
                </DialogContent>
            </StyledDialog>
            <CommonBookSession
                bookingUrl={process.env.REACT_APP_CALENDLY_SESSION_URL}
                open={open}
                setOpen={setOpen}
                studentName={`${user?.first_name} ${user?.last_name}`}
                email={user?.email}
                mentorProfileId={mentorId}
                handleClose={(data) => {
                    setOpen(false);
                    setInitialData(data);
                }}
            />
            <PageHeader
                title={location.pathname === SkillPath ? 'My DCP Skills Training Tracker' : `My DCP ${tutoringType}`}
                content={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                            My DCP Services
                        </Typography>
                        <FiberManualRecord sx={{ fontSize: 8, color: 'text.secondary' }} />
                        <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                            {location.pathname === SkillPath ? 'My DCP Skills Training Tracker' : `My DCP ${tutoringType}`}
                        </Typography>
                    </Stack>
                }
                buttonContent={
                    (assigned !== 'DISCOVERY' && user?.user_type_id === UserTypeId.Student) && (
                        <CommonButton
                            width="165px"
                            label="Book New Session"
                            fontWeight={600}
                            startIcon={<Add />}
                            marginBottom="0px"
                            handleClick={() => {
                                setOpen(true);
                            }}
                        />
                    )}
            />
            {((assignSkill.length > 0 && location.pathname === SkillPath) || location.pathname !== SkillPath) ? (
                <Grid container spacing={2} mt={2} mb={2}>
                    <Grid item md={8}>
                        {location.pathname === SkillPath ?
                            <Grid container spacing={1}>
                                {assignSkill.length > 0 && assignSkill.map((a) => (
                                    <Grid item sm={12} md={2}>
                                        <CommonButton
                                            label={a?.subject?.name}
                                            width="150px"
                                            fontWeight={600}
                                            border={subService !== a.student_subject_id ? `1px solid #0000001A` : 'none'}
                                            color={subService === a.student_subject_id ? theme.palette.secondary.main : 'gray'}
                                            background={subService === a.student_subject_id ? theme.palette.primary.main : '#FAFAFA'}
                                            handleClick={() => setSubService(a.student_subject_id)}
                                            marginBottom="0px"
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                            : (
                                <Box display="flex" gap={1}>
                                    <CommonButton
                                        label={TutoringType.AP}
                                        width="120px"
                                        fontWeight={600}
                                        color={tutoringType === TutoringType.AP ? theme.palette.secondary.main : theme.palette.background.dark}
                                        background={tutoringType === TutoringType.AP ? theme.palette.primary.main : 'white'}
                                        handleClick={() => setTutoringType(TutoringType.AP)}
                                        marginBottom="0px"
                                    />
                                    <CommonButton
                                        label={TutoringType.ACADEMIC}
                                        width="160px"
                                        fontWeight={600}
                                        color={tutoringType === TutoringType.ACADEMIC ? theme.palette.secondary.main : theme.palette.background.dark}
                                        background={tutoringType === TutoringType.ACADEMIC ? theme.palette.primary.main : 'white'}
                                        handleClick={() => setTutoringType(TutoringType.ACADEMIC)}
                                        marginBottom="0px"
                                    />
                                </Box>
                            )}
                        <ProgrssBar progressData={progressData} />
                        <Tutor
                            pathname={location.pathname}
                            tutoringType={tutoringType}
                            assigned={assigned}
                            mentorId={mentorId}
                            subService={subService}
                            setInitialData={setInitialData}
                        />
                        <SubjectOverview tutoringType={tutoringType} getStats={getStats} pathname={location.pathname} getAllAssignSubjects={getAssignedSubject} />
                        <Progress pathname={location.pathname} tutoringType={tutoringType} subService={subService} />
                        <SessionHistory tutoringType={tutoringType} subService={subService} />
                    </Grid>
                    <Grid item md={4}>
                        <CurrentPlan
                            examType={location.pathname === SkillPath ? ExamTypes.SKILLS : ExamTypes.APEXAM}
                        />
                        <Events
                            tutoringType={tutoringType}
                            studentId={user?.student_id}
                            pathname={location.pathname}
                            subService={subService}
                            setInitialData={setInitialData}
                        />
                        <Tasks tutoringType={tutoringType} subService={subService} />
                        <Resource tutoringType={tutoringType} subService={subService} />
                    </Grid>
                </Grid>
            ) : (
                <Box
                    sx={{
                        mt: 2,
                        height: '30vh',
                        background: '#7A869126',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 2
                    }}
                >
                    <Typography color={theme.palette.background.dark}>You have not taken DCP Skill Coach Service</Typography>
                    <CommonButton
                        label="Add Skill"
                        fontWeight={600}
                        width="150px"
                        marginTop={1.5}
                        handleClick={() => {
                            setAddOpen(true);
                        }}
                    />
                </Box>
            )}
        </Fragment>
    )
}

export default DcpTutoring;